import { isAxiosError } from 'axios';

interface ApiViolation {
  propertyPath: string;
  message: string;
}

interface ApiErrorResponse {
  'hydra:description'?: string;
  detail?: string;
  message?: string;
  violations?: ApiViolation[];
}

/** Trennzeichen der einzeiligen Variante -- eine Meldung, mehrere Saetze hintereinander. */
const MESSAGE_SEPARATOR = ', ';

/**
 * Extracts the individual user-facing messages from an API error response.
 * Handles both JSON-LD (Hydra) and plain JSON API Platform formats.
 *
 * Die listenwertige Variante ist die primaere: eine 422-Antwort traegt je Verstoss eine
 * eigene Meldung, und untereinander gesetzt sind sie lesbar, waehrend sie verkettet zu
 * einem unlesbaren Bandwurm werden (bei den Unfalldaten koennen es zweistellig viele
 * sein). Aufrufer, die weiterhin genau einen String brauchen (Notifications, einzeilige
 * Formularfehler), benutzen `getApiErrorMessage` -- das setzt auf dieser Funktion auf,
 * damit es nur EINE Auswertungslogik gibt.
 *
 * Rueckgabe ist immer nicht leer: ohne verwertbare Angabe steht der Ersatztext darin.
 *
 * Gleichlautende Meldungen erscheinen nur EINMAL, in der Reihenfolge ihres ersten
 * Auftretens. Eine Regel kann an mehreren Feldern haengen und ist dann allgemeingueltig
 * formuliert -- die K8-Regel der Unfalldaten ("Arbeitsausfalltage duerfen nicht kleiner
 * sein als ...") haengt an drei Beschaeftigtengruppen. In der zusammenfassenden Box waere
 * derselbe Satz mehrfach reine Wiederholung; welches Feld gemeint ist, sagt ohnehin erst
 * die Markierung am Feld. Die Entdopplung sitzt bewusst HIER und nicht an der Anzeige,
 * damit jede Box denselben Massstab hat. `mapViolations` (Feldmarkierungen) bleibt davon
 * unberuehrt -- dort ist der Feldpfad der Schluessel, jedes betroffene Feld behaelt seine
 * eigene Meldung.
 */
export function getApiErrorMessages(
  error: unknown,
  fallback = 'Ein Fehler ist aufgetreten'
): string[] {
  if (!isAxiosError(error)) {
    return [fallback];
  }

  const data = error.response?.data as ApiErrorResponse | undefined;

  if (!data) {
    return [fallback];
  }

  // Check for validation violations (API Platform validation errors)
  if (data.violations && data.violations.length > 0) {
    // Set haelt die Einfuegereihenfolge -- der erste Treffer bestimmt die Position.
    return [...new Set(data.violations.map((v) => v.message))];
  }

  // Check various error message formats
  return [data['hydra:description'] || data.detail || data.message || fallback];
}

/**
 * Die rohen Feldverstoesse einer 422-Antwort -- Gegenstueck zu `getApiErrorMessages`, das
 * dieselbe Antwort auf reine Meldungstexte reduziert. Fuer Feldmarkierungen wird der
 * `propertyPath` gebraucht, deshalb dieser zweite Zugang auf dieselben Daten.
 *
 * Leeres Array, wenn die Antwort keine Verstoesse traegt (Netzwerkfehler, 500er): der Aufrufer
 * zeigt dann nur die zusammenfassende Meldung.
 */
export function getApiViolations(error: unknown): ApiViolation[] {
  if (!isAxiosError(error)) {
    return [];
  }
  const data = error.response?.data as ApiErrorResponse | undefined;
  return data?.violations ?? [];
}

/**
 * Extracts a user-friendly error message from an API error response.
 * Handles both JSON-LD (Hydra) and plain JSON API Platform formats.
 */
export function getApiErrorMessage(error: unknown, fallback = 'Ein Fehler ist aufgetreten'): string {
  return getApiErrorMessages(error, fallback).join(MESSAGE_SEPARATOR);
}
